import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { useLocale } from "@/i18n/LocaleProvider";

export type RelatedKnowledgeItem = {
  slug: string;
  title: string;
  excerpt: string;
};

export function RelatedKnowledge({
  eyebrow,
  title,
  items,
}: {
  eyebrow?: string;
  title?: string;
  items: RelatedKnowledgeItem[];
}) {
  const en = useLocale() === "en";
  if (!items?.length) return null;
  const eb = eyebrow ?? (en ? "Go deeper" : "Fördjupa dig");
  const t = title ?? (en ? "Related knowledge" : "Relaterad kunskap");
  return (
    <Section className="bg-surface/40">
      <div className="mb-10 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-mint">{eb}</div>
          <h2 className="mt-3 text-balance text-3xl font-semibold text-butter md:text-4xl">{t}</h2>
        </div>
        <Link
          to={en ? "/en/knowledge" : "/kunskap"}
          className="hidden text-sm text-muted-foreground transition-colors hover:text-mint md:inline-flex"
        >
          {en ? "All articles →" : "Alla artiklar →"}
        </Link>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {items.map((item) => (
          <Link
            key={item.slug}
            to={en ? "/kunskap/$slug" : "/kunskap/$slug"}
            params={{ slug: item.slug }}
            className="group relative flex flex-col justify-between gap-6 rounded-2xl border border-white/10 bg-background/60 p-6 transition-all hover:-translate-y-1 hover:border-mint/40 hover:bg-background"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-mint/80">
                {en ? "Knowledge" : "Kunskap"}
              </div>
              <h3 className="mt-3 text-lg font-semibold text-butter">{item.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{item.excerpt}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-mint">
              {en ? "Read the guide" : "Läs guiden"}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </Link>
        ))}
      </div>
    </Section>
  );
}
